import React, { useState, useEffect } from 'react';
import { Settings, Save, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const TradingConfig = ({ config, onUpdate }) => {
  const [formData, setFormData] = useState({
    enabled: false,
    leverage_min: 5,
    leverage_max: 25,
    take_profit: 15.0,
    stop_loss: 50.0,
    trailing_stop: 0.5,
    max_positions: 10,
    risk_per_trade: 1.0,
    trading_pairs: []
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const allPairs = [
    // Tier 1 - Premium Assets (5-10x)
    "BTC/USDT", "ETH/USDT", "BNB/USDT", "SOL/USDT", "ADA/USDT",
    // Tier 2 - Growth Assets (8-12x)
    "AVAX/USDT", "DOT/USDT", "LINK/USDT", "UNI/USDT", "LTC/USDT",
    // Tier 3 - Opportunity Assets (10-15x)
    "XRP/USDT", "MATIC/USDT", "ATOM/USDT", "FTM/USDT", "NEAR/USDT",
    // Tier 4 - Speculative Assets (12-25x)
    "DOGE/USDT", "SHIB/USDT", "TRX/USDT", "XLM/USDT", "VET/USDT"
  ];

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePairToggle = (pair) => {
    setFormData(prev => ({
      ...prev,
      trading_pairs: prev.trading_pairs.includes(pair)
        ? prev.trading_pairs.filter(p => p !== pair)
        : [...prev.trading_pairs, pair]
    }));
  };

  const handleSelectAllPairs = () => {
    setFormData(prev => ({
      ...prev,
      trading_pairs: prev.trading_pairs.length === allPairs.length ? [] : [...allPairs]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post('/api/trading/config', formData);
      setSuccess(true);
      onUpdate && onUpdate();
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Settings className="w-6 h-6 text-blue-400 mr-2" />
          <h2 className="text-xl font-semibold text-white">Trading Configuration</h2>
        </div>
        
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Configuration
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 bg-green-900/50 border border-green-700 rounded-lg p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-green-400 mr-3" />
          <span className="text-green-300">Configuration saved successfully!</span>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-900/50 border border-red-700 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* General Settings */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-white mb-4">General Settings</h3>
          
          {/* Trading Enabled */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700"
              />
              <span className="ml-2 text-white">Enable Trading</span>
            </label>
            <p className="text-gray-400 text-sm mt-1">Allow the bot to execute trades automatically</p>
          </div>

          {/* Leverage Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Min Leverage
              </label>
              <input
                type="number"
                min="1"
                max="25"
                value={formData.leverage_min}
                onChange={(e) => handleInputChange('leverage_min', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Max Leverage
              </label>
              <input
                type="number"
                min="1"
                max="25"
                value={formData.leverage_max}
                onChange={(e) => handleInputChange('leverage_max', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Take Profit / Stop Loss */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Take Profit (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.take_profit}
                onChange={(e) => handleInputChange('take_profit', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Stop Loss (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={formData.stop_loss}
                onChange={(e) => handleInputChange('stop_loss', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Trailing Stop */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trailing Stop (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="10"
              value={formData.trailing_stop}
              onChange={(e) => handleInputChange('trailing_stop', parseFloat(e.target.value))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-gray-400 text-sm mt-1">Dynamic trailing stop percentage</p>
          </div>

          {/* Risk Management */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Max Positions
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.max_positions}
                onChange={(e) => handleInputChange('max_positions', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Risk per Trade (%)
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                max="10"
                value={formData.risk_per_trade}
                onChange={(e) => handleInputChange('risk_per_trade', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Trading Pairs */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-white">Trading Pairs</h3>
            <button
              onClick={handleSelectAllPairs}
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              {formData.trading_pairs.length === allPairs.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          <div className="space-y-4">
            {/* Tier 1 */}
            <div>
              <h4 className="text-sm font-medium text-yellow-400 mb-2">Tier 1 - Premium Assets (5-10x)</h4>
              <div className="grid grid-cols-2 gap-2">
                {allPairs.slice(0, 5).map(pair => (
                  <label key={pair} className="flex items-center p-2 bg-yellow-500/10 rounded border border-yellow-500/30">
                    <input
                      type="checkbox"
                      checked={formData.trading_pairs.includes(pair)}
                      onChange={() => handlePairToggle(pair)}
                      className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700"
                    />
                    <span className="ml-2 text-white text-sm">{pair.replace('/USDT', '')}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tier 2 */}
            <div>
              <h4 className="text-sm font-medium text-green-400 mb-2">Tier 2 - Growth Assets (8-12x)</h4>
              <div className="grid grid-cols-2 gap-2">
                {allPairs.slice(5, 10).map(pair => (
                  <label key={pair} className="flex items-center p-2 bg-green-500/10 rounded border border-green-500/30">
                    <input
                      type="checkbox"
                      checked={formData.trading_pairs.includes(pair)}
                      onChange={() => handlePairToggle(pair)}
                      className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700"
                    />
                    <span className="ml-2 text-white text-sm">{pair.replace('/USDT', '')}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tier 3 */}
            <div>
              <h4 className="text-sm font-medium text-blue-400 mb-2">Tier 3 - Opportunity Assets (10-15x)</h4>
              <div className="grid grid-cols-2 gap-2">
                {allPairs.slice(10, 15).map(pair => (
                  <label key={pair} className="flex items-center p-2 bg-blue-500/10 rounded border border-blue-500/30">
                    <input
                      type="checkbox"
                      checked={formData.trading_pairs.includes(pair)}
                      onChange={() => handlePairToggle(pair)}
                      className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700"
                    />
                    <span className="ml-2 text-white text-sm">{pair.replace('/USDT', '')}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Tier 4 */}
            <div>
              <h4 className="text-sm font-medium text-purple-400 mb-2">Tier 4 - Speculative Assets (12-25x)</h4>
              <div className="grid grid-cols-2 gap-2">
                {allPairs.slice(15, 20).map(pair => (
                  <label key={pair} className="flex items-center p-2 bg-purple-500/10 rounded border border-purple-500/30">
                    <input
                      type="checkbox"
                      checked={formData.trading_pairs.includes(pair)}
                      onChange={() => handlePairToggle(pair)}
                      className="rounded border-gray-600 text-blue-600 focus:ring-blue-500 bg-gray-700"
                    />
                    <span className="ml-2 text-white text-sm">{pair.replace('/USDT', '')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-400">
            Selected: {formData.trading_pairs.length} / {allPairs.length} pairs
          </div>
        </div>
      </div>

      {/* Strategy Info */}
      <div className="mt-8 p-4 bg-blue-900/20 rounded-lg border border-blue-800">
        <h4 className="text-blue-300 font-medium mb-2">Active Strategy: Min/Max 10-Minute</h4>
        <p className="text-blue-200 text-sm">
          Automated entry on 10-minute highs/lows with technical confirmation • 
          Trailing stop protection • Multi-timeframe analysis • Risk-adjusted position sizing
        </p>
      </div>
    </div>
  );
};

export default TradingConfig;